/**
 * Export format picker: web formats and broadcast masters, with what each is and whether it fits the frame rate.
 * Asset exports (0.99.0) also offer the original's own format first, with no transcode; for assets that are not
 * video it is the only choice.
 */
import { EXPORT_PRESETS, EXPORT_PRESET_IDS, presetProblem, type ExportFormatId, type ExportPreset } from '@prism/contracts';

export function PresetSelect<T extends ExportFormatId = ExportPreset>({ id, value, onChange, frameRate, original, originalOnly, originalHelp }: {
  id: string; value: T; onChange: (p: T) => void; frameRate?: { num: number; den: number } | null;
  /** The original's format ("MOV, ProRes 422 HQ"): offered first as "Original format · no transcode". */
  original?: string | null;
  /** Only the original format can be exported (images, graphics, audio, documents): the list is shown read-only. */
  originalOnly?: boolean;
  /** What the original format does with this export (whole file or rewrap, exact or widened). */
  originalHelp?: string;
}) {
  const group = (g: 'web' | 'broadcast') => EXPORT_PRESET_IDS.filter((k) => EXPORT_PRESETS[k].group === g);
  if (value === 'original') {
    return (
      <>
        <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value as T)} disabled={originalOnly} data-testid="export-preset">
          <option value="original">Original format · no transcode{original ? ` (${original})` : ''}</option>
          {!originalOnly && <Presets frameRate={frameRate} group={group} />}
        </select>
        <span className="help" data-testid="original-format-note">{originalHelp ?? 'The original, as it is'}</span>
      </>
    );
  }
  const preset = value as ExportPreset;
  const problem = frameRate ? presetProblem(preset, frameRate) : null;
  return (
    <>
      <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value as T)} data-testid="export-preset">
        {original != null && <option value="original">Original format · no transcode{original ? ` (${original})` : ''}</option>}
        <Presets frameRate={frameRate} group={group} />
      </select>
      <span className="help" style={problem ? { color: 'var(--danger)' } : undefined}>{problem ?? `${EXPORT_PRESETS[preset].description}${EXPORT_PRESETS[preset].group === 'broadcast' ? '. Downloads only; browsers cannot play it.' : ''}`}</span>
    </>
  );
}

function Presets({ frameRate, group }: { frameRate?: { num: number; den: number } | null; group: (g: 'web' | 'broadcast') => ExportPreset[] }) {
  return (
    <>
      <optgroup label="Web and review">{group('web').map((k) => <option key={k} value={k}>{EXPORT_PRESETS[k].label}</option>)}</optgroup>
      <optgroup label="Broadcast">{group('broadcast').map((k) => <option key={k} value={k} disabled={Boolean(frameRate && presetProblem(k, frameRate))}>{EXPORT_PRESETS[k].label}</option>)}</optgroup>
    </>
  );
}
